import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, fileUrl, tokenStore, API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { FileText, Folder, Package2, RotateCcw, Trash, Trash2, Upload } from "lucide-react";
import { ToolSetsHistorySection } from "../../components/ToolSetsHistorySection";
import { DocumentViewer } from "../../components/DocumentViewer";
import { UploadButton } from "../../components/AuthImg";
import { canSeeChf } from "../../lib/chf";
import { fmtRoDate, fmtRoDateTime } from "../../lib/dateFmt";
import { DropZone } from "../../components/DropZone";
import { missingProjectFields, statusBucket, DOC_CATEGORIES } from "../Proiecte";

function ProjectListRow({ r, highlightId, selectedCardId, onSelect, canDo, setDocsFor, setHoursReportFor, edit, remove, markFinalizat, markDemontat, reopenProject, siteCount, navigate }) {
  const sb = statusBucket(r);
  const today = new Date().toISOString().slice(0, 10);
  const notStarted = sb === "in_lucru" && r.start_date && r.start_date > today;
  const missing = missingProjectFields(r);
  return (
    <div
      onClick={(e) => {
        const tgt = e.target;
        if (tgt.closest && tgt.closest("button, a, input, label, select, textarea, [role='button']")) return;
        onSelect && onSelect();
      }}
      className={`grid grid-cols-1 md:grid-cols-[1fr_180px_180px_140px_140px_120px] gap-3 px-4 py-2.5 border-b border-zinc-100 hover:bg-zinc-50 transition-colors items-center text-sm cursor-pointer ${
        selectedCardId === r.id
          ? "bg-blue-50 ring-2 ring-blue-500"
          : highlightId === r.id ? "bg-amber-50 ring-2 ring-amber-300" : ""
      }`}
      data-testid={`project-row-${r.id}`}
    >
      {/* Proiect */}
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Folder size={14} className="text-[var(--primary)] shrink-0"/>
          <button
            onClick={() => canDo("PROIECTE", "edit") && edit(r)}
            className="font-bold truncate hover:text-[var(--primary)] text-left"
            title={r.name}
          >{r.name}</button>
          {missing.length > 0 && (
            <span title={`Lipsesc: ${missing.map((m) => m.label).join(", ")}`} className="bg-amber-100 text-amber-700 text-[9px] font-bold px-1.5 py-0.5 rounded animate-pulse">
              {missing.length} lipsă
            </span>
          )}
        </div>
        {r.adresa && <div className="text-[11px] text-zinc-500 truncate ml-5">📍 {r.adresa}</div>}
      </div>
      {/* Auftraggeber */}
      <div className="text-xs text-zinc-700 truncate" title={r.auftraggeber || "—"}>
        {r.auftraggeber || <span className="text-zinc-400">—</span>}
        {r.auftraggeber_contact && <div className="text-[10px] text-zinc-500">👤 {r.auftraggeber_contact}</div>}
      </div>
      {/* Bauleiter */}
      <div className="text-xs text-zinc-700 truncate" title={r.bauleiter || "—"}>
        {r.bauleiter || <span className="text-zinc-400">—</span>}
      </div>
      {/* Perioadă */}
      <div className="text-[11px] text-zinc-600 font-mono whitespace-nowrap">
        {r.indeterminate_start ? (
          <span className="text-amber-700 font-semibold" title="Start nedeterminat">♾ TBD</span>
        ) : (r.start_date || "—")}<br/>{r.end_date || "—"}
      </div>
      {/* Status */}
      <div>
        {notStarted ? (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-semibold whitespace-nowrap" title={`Începe la ${fmtRoDate(r.start_date)}`}>⏳ {r.status}</span>
        ) : (
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${
            sb === "de_demontat" ? "bg-orange-100 text-orange-700 animate-pulse"
            : sb === "demontat" ? "bg-zinc-100 text-zinc-600"
            : "bg-[var(--primary-subtle)] text-[var(--primary)]"
          }`}>{r.status}</span>
        )}
        {sb === "de_demontat" && siteCount && (siteCount.on_site || 0) > 0 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate && navigate("/inventar/santier"); }}
            className="ml-1 mt-0.5 text-[9px] px-1.5 py-0.5 rounded font-bold bg-orange-600 text-white hover:bg-orange-700 transition-colors inline-flex items-center gap-0.5 animate-pulse"
            title="Echipamente lăsate pe șantier"
            data-testid={`project-row-leftovers-${r.id}`}
          >
            🚧 {siteCount.on_site}
          </button>
        )}
        {r.min_people > 0 && (
          <div className="text-[9px] text-orange-700 mt-0.5">
            {r.max_people && r.max_people !== r.min_people ? `${r.min_people}-${r.max_people}` : r.min_people} pers.
          </div>
        )}
      </div>
      {/* Acțiuni */}
      <div className="flex items-center justify-end gap-1.5 flex-wrap">
        <button
          onClick={() => setDocsFor(r)}
          className="text-[var(--primary)] hover:underline inline-flex items-center gap-0.5 text-xs"
          title="Documente"
          data-testid={`row-docs-${r.id}`}
        ><FileText size={12}/></button>
        <button
          onClick={() => setHoursReportFor(r)}
          className="text-zinc-500 hover:text-[var(--primary)] text-xs"
          title="Ore lucrate"
        >⏱️</button>
        {canDo("PROIECTE", "edit") && (
          <button
            onClick={() => edit(r)}
            className="text-[var(--primary)] hover:underline text-xs"
            title="Editează"
          >✎</button>
        )}
        {canDo("PROIECTE", "edit") && sb === "in_lucru" && (
          <button
            onClick={(e) => { e.stopPropagation(); markFinalizat(r); }}
            className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-orange-600 text-white hover:bg-orange-700 transition-colors"
            title="Finalizat — trece la 'De demontat'"
            data-testid={`row-mark-finalizat-${r.id}`}
          >🏁 FINALIZEAZĂ</button>
        )}
        {canDo("PROIECTE", "edit") && sb === "de_demontat" && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); markDemontat(r); }}
              className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-zinc-700 text-white hover:bg-zinc-800 transition-colors"
              title="Demontat — trece în categoria 7 (de facturat)"
              data-testid={`row-mark-demontat-${r.id}`}
            >🔧 DEMONTEAZĂ</button>
            <button
              onClick={(e) => { e.stopPropagation(); reopenProject(r); }}
              className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 transition-colors inline-flex items-center gap-0.5"
              title="Trimite înapoi la 'În lucru'"
              data-testid={`row-reopen-project-${r.id}`}
            >
              <RotateCcw size={10}/> ÎN LUCRU
            </button>
          </>
        )}
        {canDo("PROIECTE", "delete") && (
          <button
            onClick={() => remove(r.id)}
            className="text-red-600 p-0.5 hover:bg-red-50 rounded"
            title="Șterge"
          ><Trash2 size={12}/></button>
        )}
      </div>
    </div>
  );
}



function HoursReportDialog({ project, onClose }) {
  const { user } = useAuth();
  const chfOk = canSeeChf(user);
  const hideChf = !chfOk;
  const isAdmin = chfOk; // tab-ul Audit conține salarii → doar cei cu drept CHF
  const monthOpts = (() => {
    const out = [{ value: "all", label: "Toate" }];
    const now = new Date();
    for (let i = 0; i < 12; i += 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleDateString("ro-RO", { year: "numeric", month: "long" }) });
    }
    return out;
  })();
  const [month, setMonth] = useState("all");
  const [tab, setTab] = useState("efectiv");
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState(null); // { exists: false } sau snapshot
  const [auditRows, setAuditRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingAudit, setSavingAudit] = useState(false);

  const loadEfectiv = async () => {
    setLoading(true);
    const url = `/projects/${project.id}/hours-report${month === "all" ? "" : `?month=${month}`}`;
    try {
      const res = await api.get(url);
      setData(res.data);
    } catch { toast.error("Eroare"); }
    finally { setLoading(false); }
  };
  const loadAudit = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const res = await api.get(`/projects/${project.id}/hours-report-audit?month=${month}`);
      setAudit(res.data);
      setAuditRows(res.data?.rows || []);
    } catch { setAudit({ exists: false }); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (tab === "efectiv") loadEfectiv();
    else loadAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id, month, tab]);

  const createSnapshot = async () => {
    setSavingAudit(true);
    try {
      const res = await api.post(`/projects/${project.id}/hours-report-audit?month=${month}`);
      setAudit(res.data);
      setAuditRows(res.data?.rows || []);
      toast.success("Snapshot audit creat");
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare la creare"); }
    finally { setSavingAudit(false); }
  };
  const deleteSnapshot = async () => {
    if (!window.confirm("Șterge snapshot-ul audit? Datele audit pentru luna asta vor dispărea.")) return;
    setSavingAudit(true);
    try {
      await api.delete(`/projects/${project.id}/hours-report-audit?month=${month}`);
      setAudit({ exists: false });
      setAuditRows([]);
      toast.success("Snapshot șters");
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare ștergere"); }
    finally { setSavingAudit(false); }
  };
  const saveAudit = async () => {
    setSavingAudit(true);
    try {
      const res = await api.put(`/projects/${project.id}/hours-report-audit?month=${month}`, { rows: auditRows });
      setAudit(res.data);
      setAuditRows(res.data.rows || []);
      toast.success("Modificări audit salvate");
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare salvare"); }
    finally { setSavingAudit(false); }
  };
  const updateAuditRow = (idx, field, val) => {
    setAuditRows((prev) => {
      const next = [...prev];
      const numeric = ["hours", "days", "hourly_rate", "salary"].includes(field);
      next[idx] = { ...next[idx], [field]: numeric ? (val === "" ? 0 : parseFloat(val) || 0) : val };
      // Auto-calculate salary if hours/rate changed
      if (field === "hours" || field === "hourly_rate") {
        next[idx].salary = parseFloat((next[idx].hours * next[idx].hourly_rate).toFixed(2));
      }
      return next;
    });
  };
  const auditTotals = (() => {
    return {
      hours: auditRows.reduce((s, r) => s + (r.hours || 0), 0),
      days: auditRows.reduce((s, r) => s + (r.days || 0), 0),
      salary: auditRows.reduce((s, r) => s + (r.salary || 0), 0),
    };
  })();

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="hours-report-dialog">
        <DialogHeader>
          <DialogTitle>⏱️ Ore lucrate · {project.title || project.name}</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <Label className="small-label">Luna:</Label>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-56 h-9"><SelectValue/></SelectTrigger>
            <SelectContent>
              {monthOpts.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {isAdmin ? (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="efectiv" data-testid="tab-efectiv">📊 Date efective</TabsTrigger>
              <TabsTrigger value="audit" data-testid="tab-audit">📋 Date audit</TabsTrigger>
            </TabsList>
            <TabsContent value="efectiv">
              <EfectivTable data={data} loading={loading} hideChf={hideChf} month={month}/>
            </TabsContent>
            <TabsContent value="audit">
              {loading ? (
                <div className="text-sm text-zinc-400 italic py-6 text-center">Se încarcă…</div>
              ) : !audit?.exists ? (
                <div className="card-flat p-8 text-center bg-amber-50 border border-amber-200 mt-3">
                  <div className="text-amber-900 font-bold mb-2">📸 Niciun snapshot audit pentru luna selectată</div>
                  <div className="text-xs text-amber-800 mb-4">
                    Snapshot-ul copiază datele efective într-o variantă editabilă, separată complet de datele reale. Util pentru a prezenta cifre rotunjite/ajustate la audit fără să afectezi raportul real.
                  </div>
                  <Button
                    onClick={createSnapshot}
                    disabled={savingAudit}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="create-audit-snapshot-btn"
                  >
                    {savingAudit ? "Se creează…" : "📸 Creează snapshot audit"}
                  </Button>
                </div>
              ) : (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div className="text-xs text-zinc-500 italic">
                      Snapshot creat: {audit.created_at?.slice(0, 16).replace("T", " ")} de {audit.created_by} · Modificat: {audit.updated_at?.slice(0, 16).replace("T", " ")}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={deleteSnapshot} disabled={savingAudit} className="border-red-300 text-red-700 hover:bg-red-50 text-xs" data-testid="delete-audit-snapshot-btn">
                        <Trash2 size={12} className="mr-1"/> Șterge snapshot
                      </Button>
                      <Button size="sm" onClick={saveAudit} disabled={savingAudit} className="bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="save-audit-btn">
                        {savingAudit ? "Se salvează…" : "💾 Salvează modificări"}
                      </Button>
                    </div>
                  </div>
                  <div className="card-flat p-0 overflow-hidden max-h-[60vh] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-amber-50 border-b border-amber-200 sticky top-0">
                        <tr>
                          <th className="p-2 text-left text-xs font-semibold text-amber-900 uppercase">Persoană</th>
                          <th className="p-2 text-right text-xs font-semibold text-amber-900 uppercase">Ore</th>
                          <th className="p-2 text-right text-xs font-semibold text-amber-900 uppercase">Zile</th>
                          <th className="p-2 text-right text-xs font-semibold text-amber-900 uppercase">Tarif (CHF)</th>
                          <th className="p-2 text-right text-xs font-semibold text-amber-900 uppercase">Salar (CHF)</th>
                          <th className="p-2 text-left text-xs font-semibold text-amber-900 uppercase">Notă</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditRows.length === 0 && (
                          <tr><td colSpan={6} className="p-4 text-center text-zinc-400 italic">Niciun rând în snapshot.</td></tr>
                        )}
                        {auditRows.map((r, i) => (
                          <tr key={r.person_id} className="border-b border-[var(--border)] last:border-b-0">
                            <td className="p-2 font-medium">{r.person_name}</td>
                            <td className="p-2"><Input type="number" step="0.25" min="0" value={r.hours} onChange={(e) => updateAuditRow(i, "hours", e.target.value)} className="w-20 h-8 text-right tabular-nums" data-testid={`audit-hours-${r.person_id}`}/></td>
                            <td className="p-2"><Input type="number" step="1" min="0" value={r.days} onChange={(e) => updateAuditRow(i, "days", e.target.value)} className="w-16 h-8 text-right tabular-nums" data-testid={`audit-days-${r.person_id}`}/></td>
                            <td className="p-2"><Input type="number" step="0.5" min="0" value={r.hourly_rate} onChange={(e) => updateAuditRow(i, "hourly_rate", e.target.value)} className="w-24 h-8 text-right tabular-nums" data-testid={`audit-rate-${r.person_id}`}/></td>
                            <td className="p-2"><Input type="number" step="0.01" min="0" value={r.salary} onChange={(e) => updateAuditRow(i, "salary", e.target.value)} className="w-28 h-8 text-right tabular-nums font-bold" data-testid={`audit-salary-${r.person_id}`}/></td>
                            <td className="p-2"><Input value={r.note || ""} onChange={(e) => updateAuditRow(i, "note", e.target.value)} placeholder="opțional" className="h-8" data-testid={`audit-note-${r.person_id}`}/></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-amber-50 border-t-2 border-amber-200">
                        <tr>
                          <td className="p-2 font-bold text-amber-900">TOTAL</td>
                          <td className="p-2 text-right tabular-nums font-bold">{auditTotals.hours.toFixed(2)}</td>
                          <td className="p-2 text-right tabular-nums font-bold">{auditTotals.days}</td>
                          <td className="p-2"></td>
                          <td className="p-2 text-right tabular-nums font-bold text-[var(--primary)]">{auditTotals.salary.toFixed(2)}</td>
                          <td className="p-2"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        ) : (
          <EfectivTable data={data} loading={loading} hideChf={hideChf} month={month}/>
        )}

        <DialogFooter><Button variant="outline" onClick={onClose}>Închide</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EfectivTable({ data, loading, hideChf, month }) {
  return (
    <>
      {data && (
        <div className="flex gap-3 text-sm flex-wrap mb-2">
          <div><span className="text-zinc-500">Ore:</span> <strong>{data.total_hours}</strong></div>
          <div><span className="text-zinc-500">Zile:</span> <strong>{data.total_days}</strong></div>
          {!hideChf && data.total_salary != null && (
            <div><span className="text-zinc-500">Salar:</span> <strong className="text-[var(--primary)]">{data.total_salary.toFixed(2)} CHF</strong></div>
          )}
        </div>
      )}
      {loading ? <div className="text-sm text-zinc-400 italic py-6 text-center">Se încarcă…</div> : (
        <div className="card-flat p-0 overflow-hidden mt-3 max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-[var(--border)] sticky top-0">
              <tr>
                <th className="p-2 text-left text-xs font-semibold text-zinc-500 uppercase">Persoană</th>
                <th className="p-2 text-right text-xs font-semibold text-zinc-500 uppercase">Ore</th>
                <th className="p-2 text-right text-xs font-semibold text-zinc-500 uppercase">Zile</th>
                {!hideChf && <th className="p-2 text-right text-xs font-semibold text-zinc-500 uppercase">Salar (CHF)</th>}
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).length === 0 && (
                <tr><td colSpan={hideChf ? 3 : 4} className="p-4 text-center text-zinc-400 italic">Nicio oră raportată pe acest proiect{month !== "all" ? " în luna selectată" : ""}.</td></tr>
              )}
              {(data?.rows || []).map((r) => (
                <tr key={r.person_id} className="border-b border-[var(--border)] last:border-b-0">
                  <td className="p-2 font-medium">{r.person_name}</td>
                  <td className="p-2 text-right tabular-nums">{r.hours}</td>
                  <td className="p-2 text-right tabular-nums text-zinc-600">{r.days}</td>
                  {!hideChf && <td className="p-2 text-right tabular-nums font-bold text-[var(--primary)]">{r.salary != null ? r.salary.toFixed(2) : "—"}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ProjectDocsDialog({ project, onClose, onProjectUpdated, canEdit }) {
  const [attachments, setAttachments] = useState([]);
  const [preview, setPreview] = useState(null); // { fileId, name, ctype } | null
  const [bagsLog, setBagsLog] = useState([]);
  const [bagsForm, setBagsForm] = useState({ festgebunden: "", schwachgebunden: "", note: "" });
  const [uploadingCat, setUploadingCat] = useState(null);
  const [photoPreviewIdx, setPhotoPreviewIdx] = useState(null);  // index in poze list

  const loadData = async () => {
    const [a, b] = await Promise.all([
      api.get(`/projects/${project.id}/attachments`),
      api.get(`/projects/${project.id}/bags-log`),
    ]);
    setAttachments(a.data || []);
    setBagsLog(b.data || []);
  };
  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [project.id]);

  const uploadFile = async (file, category, { silent = false } = {}) => {
    if (!file) return false;
    setUploadingCat(category);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: f } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/projects/${project.id}/attachments`, { file_id: f.id, category });
      if (!silent) toast.success("Fișier încărcat");
      loadData();
      return true;
    } catch (e) {
      toast.error(`${file.name}: ${e?.response?.data?.detail || "Eroare upload"}`);
      return false;
    } finally { setUploadingCat(null); }
  };

  const removeAttachment = async (a) => {
    if (!window.confirm(`Șterge fișierul „${a.filename}"?`)) return;
    await api.delete(`/projects/${project.id}/attachments/${a.id}`);
    loadData();
  };

  const submitBags = async () => {
    const fest = parseInt(bagsForm.festgebunden) || 0;
    const schwach = parseInt(bagsForm.schwachgebunden) || 0;
    if (fest === 0 && schwach === 0) { toast.error("Adaugă cel puțin un sac"); return; }
    try {
      const { data } = await api.post(`/projects/${project.id}/bags-add`, { festgebunden: fest, schwachgebunden: schwach, note: bagsForm.note });
      toast.success(`+${fest} festgebunden / +${schwach} schwachgebunden`);
      setBagsForm({ festgebunden: "", schwachgebunden: "", note: "" });
      onProjectUpdated(data.project);
      loadData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const fmtSize = (s) => {
    if (!s) return "";
    if (s < 1024) return `${s} B`;
    if (s < 1024 * 1024) return `${(s / 1024).toFixed(1)} KB`;
    return `${(s / 1024 / 1024).toFixed(1)} MB`;
  };

  const grouped = useMemo(() => {
    const m = {};
    for (const a of attachments) (m[a.category] = m[a.category] || []).push(a);
    return m;
  }, [attachments]);

  const downloadFile = (fileId, filename) => {
    const url = `${api.defaults.baseURL}/files/${fileId}`;
    const token = tokenStore.get();
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u; a.download = filename || "file";
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(u);
      });
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="project-docs-dialog">
        <DialogHeader>
          <DialogTitle>Documente · {project.name}</DialogTitle>
        </DialogHeader>

        {/* Bags counter */}
        <div className="card-flat p-4 bg-zinc-50">
          <div className="flex items-center gap-2 mb-3">
            <Package2 size={16} className="text-[var(--orange-bila)]"/>
            <h3 className="font-display font-bold">Saci aruncați</h3>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div className="card-flat p-3 text-center">
              <div className="small-label text-yellow-600">Festgebunden</div>
              <div className="font-display text-2xl font-extrabold text-yellow-600 mt-1" data-testid="bags-fest-total">{project.bags_festgebunden || 0}</div>
            </div>
            <div className="card-flat p-3 text-center">
              <div className="small-label text-red-600">Schwachgebunden</div>
              <div className="font-display text-2xl font-extrabold text-red-600 mt-1" data-testid="bags-schwach-total">{project.bags_schwachgebunden || 0}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div>
              <Label className="text-[11px] text-yellow-600 font-semibold">+ Festgebunden</Label>
              <Input type="number" min="0" value={bagsForm.festgebunden} onChange={(e) => setBagsForm({ ...bagsForm, festgebunden: e.target.value })} placeholder="0" data-testid="bags-add-fest"/>
            </div>
            <div>
              <Label className="text-[11px] text-red-600 font-semibold">+ Schwachgebunden</Label>
              <Input type="number" min="0" value={bagsForm.schwachgebunden} onChange={(e) => setBagsForm({ ...bagsForm, schwachgebunden: e.target.value })} placeholder="0" data-testid="bags-add-schwach"/>
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px]">Notă (opțional)</Label>
              <div className="flex gap-2">
                <Input value={bagsForm.note} onChange={(e) => setBagsForm({ ...bagsForm, note: e.target.value })} placeholder="ex: zona A, parter" />
                <Button onClick={submitBags} data-testid="bags-submit" className="bg-[var(--primary)]">Adaugă</Button>
              </div>
            </div>
          </div>
          {bagsLog.length > 0 && (
            <details className="mt-3 text-xs">
              <summary className="cursor-pointer text-[var(--primary)] font-semibold">Istoric ({bagsLog.length})</summary>
              <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {bagsLog.map((l) => (
                  <div key={l.id} className="flex items-center gap-2 text-zinc-600 border-b border-zinc-100 py-1">
                    <span className="font-mono text-[10px] text-zinc-400">{fmtRoDateTime(l.created_at)}</span>
                    <span className="font-semibold">{l.user_name}</span>
                    <span>
                      <span className="text-yellow-600 font-semibold">+{l.festgebunden}f</span>
                      <span className="text-zinc-400"> / </span>
                      <span className="text-red-600 font-semibold">{l.schwachgebunden}s</span>
                    </span>
                    {l.note && <span className="italic text-zinc-500">„{l.note}"</span>}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* File categories */}
        <div className="space-y-3 mt-4">
          {DOC_CATEGORIES.map((c) => {
            const items = grouped[c.code] || [];
            const isUploading = uploadingCat === c.code;
            return (
              <div key={c.code} className="card-flat p-3" data-testid={`docs-cat-${c.code}`}>
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{c.icon}</span>
                    <span className="font-semibold text-sm">{c.label}</span>
                    <span className="text-[10px] text-zinc-400">({items.length})</span>
                  </div>
                  {canEdit && (
                    <label className="text-xs px-3 py-1.5 rounded-md border border-dashed border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary-subtle)] cursor-pointer inline-flex items-center gap-1.5 transition-colors">
                      <Upload size={12}/>
                      {isUploading ? "Se încarcă…" : (c.code === "poze" ? "Încarcă poze" : "Încarcă fișiere")}
                      <input
                        type="file"
                        className="hidden"
                        accept={c.code === "poze" ? "image/*" : undefined}
                        multiple
                        disabled={isUploading}
                        onChange={async (e) => {
                          const files = Array.from(e.target.files || []);
                          if (files.length === 0) return;
                          const multi = files.length > 1;
                          if (multi) toast.info(`Se încarcă ${files.length} fișiere…`);
                          let okCount = 0;
                          for (const f of files) {
                            // eslint-disable-next-line no-await-in-loop
                            const ok = await uploadFile(f, c.code, { silent: multi });
                            if (ok) okCount += 1;
                          }
                          if (multi) {
                            if (okCount === files.length) toast.success(`${okCount} fișiere încărcate`);
                            else toast.warning(`${okCount}/${files.length} fișiere încărcate`);
                          }
                          e.target.value = "";
                        }}
                        data-testid={`upload-${c.code}`}
                      />
                    </label>
                  )}
                </div>
                {items.length === 0 ? (
                  <div className="text-xs text-zinc-400 italic py-1">Niciun fișier încărcat în această categorie.</div>
                ) : c.code === "poze" ? (
                  /* Thumbnail gallery for photos */
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {items.map((a, idx) => (
                      <div key={a.id} className="relative group border border-zinc-200 rounded-md overflow-hidden bg-zinc-50">
                        <img
                          src={fileUrl(a.file_id)}
                          alt={a.filename}
                          className="w-full h-24 object-cover cursor-pointer hover:opacity-90"
                          onClick={() => setPhotoPreviewIdx(idx)}
                          data-testid={`proj-photo-thumb-${idx}`}
                        />
                        <div className="absolute bottom-0 left-0 right-0 px-1.5 py-0.5 bg-black/55 text-white text-[9px] truncate" title={a.filename}>
                          {a.filename}
                        </div>
                        <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => { e.stopPropagation(); downloadFile(a.file_id, a.filename); }}
                            className="p-1 rounded bg-white/90 hover:bg-white text-zinc-700 shadow-sm"
                            title="Descarcă"
                          ><span className="text-xs">⬇</span></button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const url = fileUrl(a.file_id);
                              const txt = `📷 ${project.name || "Proiect"}\n${a.filename}\nLink: ${url}`;
                              window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank", "noopener,noreferrer");
                            }}
                            className="p-1 rounded bg-green-500/90 hover:bg-green-500 text-white shadow-sm text-[10px] font-bold"
                            title="WhatsApp"
                          >WA</button>
                          {canEdit && (
                            <button
                              onClick={(e) => { e.stopPropagation(); removeAttachment(a); }}
                              className="p-1 rounded bg-red-500/90 hover:bg-red-500 text-white shadow-sm"
                              title="Șterge"
                            ><Trash size={10}/></button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {items.map((a) => (
                      <div key={a.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-50 text-xs">
                        <button
                          onClick={() => setPreview({ fileId: a.file_id, name: a.filename, ctype: a.content_type || "" })}
                          className="text-[var(--primary)] hover:underline truncate flex-1 text-left"
                          title={`Vizualizează ${a.filename}`}
                          data-testid={`view-attachment-${a.id}`}
                        >
                          📎 {a.filename}
                        </button>
                        <button
                          onClick={() => downloadFile(a.file_id, a.filename)}
                          className="text-zinc-500 hover:text-green-600 px-1"
                          title="Descarcă"
                          data-testid={`download-attachment-${a.id}`}
                        >
                          ⬇
                        </button>
                        <span className="text-zinc-400 whitespace-nowrap">{fmtSize(a.size)}</span>
                        <span className="text-zinc-400 whitespace-nowrap">{a.uploaded_by_name}</span>
                        <span className="text-zinc-400 whitespace-nowrap text-[10px]">{fmtRoDate(a.uploaded_at)}</span>
                        {canEdit && <button onClick={() => removeAttachment(a)} className="text-red-500 hover:text-red-700"><Trash size={11}/></button>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Închide</Button>
        </DialogFooter>
        {preview && (
          <DocumentViewer
            fileId={preview.fileId}
            name={preview.name}
            ctype={preview.ctype}
            open={!!preview}
            onOpenChange={(v) => { if (!v) setPreview(null); }}
            onDownload={() => downloadFile(preview.fileId, preview.name)}
          />
        )}
        {photoPreviewIdx !== null && (() => {
          const photos = grouped["poze"] || [];
          const cur = photos[photoPreviewIdx];
          if (!cur) return null;
          return (
            <Dialog open onOpenChange={() => setPhotoPreviewIdx(null)}>
              <DialogContent className="max-w-5xl p-0 bg-black/95 border-0">
                <button
                  onClick={() => setPhotoPreviewIdx(null)}
                  className="absolute top-4 right-4 z-10 text-white bg-black/50 hover:bg-black/70 rounded-full p-2"
                  aria-label="Închide"
                >✕</button>
                <img src={fileUrl(cur.file_id)} alt={cur.filename} className="w-full max-h-[85vh] object-contain"/>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/60 rounded-full px-2 py-1.5 text-sm">
                  <span className="text-white px-2">{photoPreviewIdx + 1}/{photos.length}</span>
                  {photoPreviewIdx > 0 && (
                    <button onClick={() => setPhotoPreviewIdx(photoPreviewIdx - 1)} className="text-white px-3 py-1 hover:bg-white/10 rounded-full">← Anterior</button>
                  )}
                  <button onClick={() => downloadFile(cur.file_id, cur.filename)} className="text-white px-3 py-1 hover:bg-white/10 rounded-full">⬇ Descarcă</button>
                  <button
                    onClick={() => {
                      const url = fileUrl(cur.file_id);
                      const txt = `📷 ${project.name || "Proiect"}\n${cur.filename}\nLink: ${url}`;
                      window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank", "noopener,noreferrer");
                    }}
                    className="text-green-400 px-3 py-1 hover:bg-white/10 rounded-full"
                  >🟢 WhatsApp</button>
                  {photoPreviewIdx < photos.length - 1 && (
                    <button onClick={() => setPhotoPreviewIdx(photoPreviewIdx + 1)} className="text-white px-3 py-1 hover:bg-white/10 rounded-full">Următor →</button>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}

// ===== PIPELINE BANNER (workflow ofertare → SUVA → Sanierungskonzept) =====

function daysBetween(d1, d2) {
  if (!d1 || !d2) return null;
  const a = new Date(d1).setHours(0, 0, 0, 0);
  const b = new Date(d2).setHours(0, 0, 0, 0);
  return Math.round((a - b) / (1000 * 60 * 60 * 24));
}

function PipelineBanner({ project, onChanged, canEdit }) {
  const { user } = useAuth();
  const hideChf = user?.role === "planificator" || user?.role === "user";
  const [busy, setBusy] = useState(false);
  const [askResp, setAskResp] = useState(false);
  const [ofertaUpload, setOfertaUpload] = useState(false);
  const [ofertaFile, setOfertaFile] = useState(null);
  const [ofertaPrice, setOfertaPrice] = useState("");
  const [suvaUpload, setSuvaUpload] = useState(false);
  const [suvaFile, setSuvaFile] = useState(null);
  const [konzeptUpload, setKonzeptUpload] = useState(false);
  const [konzeptFile, setKonzeptFile] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  const post = async (path, body) => {
    setBusy(true);
    try {
      await api.post(path, body || {});
      toast.success("Actualizat");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setBusy(false); }
  };

  const submitOferta = async () => {
    const priceNum = parseFloat(ofertaPrice);
    if (!ofertaFile && (!priceNum || priceNum <= 0)) {
      toast.error("Încarcă fișierul ofertă SAU introdu prețul");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/mark-ofertat`, {
        ...(ofertaFile ? { file_id: ofertaFile.id, file_name: ofertaFile.original_filename || ofertaFile.filename || "ofertă" } : {}),
        ...(priceNum && priceNum > 0 ? { price: priceNum } : {}),
      });
      toast.success("Marcat ca ofertat");
      setOfertaUpload(false);
      setOfertaFile(null);
      setOfertaPrice("");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setBusy(false); }
  };

  const submitFileAction = async (path, file, errLabel) => {
    if (!file) {
      toast.error(`Încarcă fișierul ${errLabel}`);
      return;
    }
    setBusy(true);
    try {
      await api.post(path, {
        file_id: file.id,
        file_name: file.original_filename || file.filename || errLabel,
      });
      toast.success("Salvat");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setBusy(false); }
  };

  const cat = String(project.category || "");

  // 1. CATEGORIA 1: De ofertat — banner cu deadline
  if (cat === "1") {
    const dl = project.oferta_deadline;
    if (!dl) return null;
    const left = daysBetween(dl, today);
    const isPast = left < 0;
    const isUrgent = left <= 3;
    const cls = isPast ? "bg-red-50 border-red-300 text-red-800"
              : isUrgent ? "bg-orange-50 border-orange-300 text-orange-800 animate-pulse"
              : "bg-amber-50 border-amber-200 text-amber-800";
    return (
      <div className={`mb-3 rounded-md border ${cls}`} data-testid={`pipeline-banner-${project.id}`}>
        <div className="px-3 py-2 flex items-center gap-2">
          <span className="text-base">{isPast ? "⏰" : "📅"}</span>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider">
              {isPast ? `Deadline depășit cu ${Math.abs(left)} zile` : left === 0 ? "🔥 Ofertare ASTĂZI" : `${left} ${left === 1 ? "zi rămasă" : "zile rămase"} pentru ofertare`}
            </div>
            <div className="text-[10px]">Limită: {dl}</div>
          </div>
          {canEdit && !ofertaUpload && (
            <Button size="sm" onClick={() => setOfertaUpload(true)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`pipeline-mark-ofertat-${project.id}`}>
              ✓ Ofertat
            </Button>
          )}
        </div>
        {ofertaUpload && (
          <div className="px-3 pb-3 pt-1 border-t border-current/20 bg-white/60 space-y-2">
            <div className="text-[11px] font-bold text-emerald-800">Încarcă oferta SAU introdu prețul (cel puțin una din variante)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="border border-emerald-200 rounded p-2 bg-white">
                <div className="text-[10px] uppercase font-bold text-emerald-700 mb-1">📎 Fișier ofertă</div>
                {ofertaFile ? (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-emerald-700 truncate">✓ {ofertaFile.original_filename || ofertaFile.filename}</span>
                    <button type="button" onClick={() => setOfertaFile(null)} className="text-red-600 underline text-[11px]">✕</button>
                  </div>
                ) : (
                  <UploadButton accept="application/pdf,image/*" label="📎 Upload" testId={`oferta-upload-${project.id}`} onUploaded={(f) => setOfertaFile(f)}/>
                )}
              </div>
              {!hideChf && (
                <div className="border border-emerald-200 rounded p-2 bg-white">
                  <div className="text-[10px] uppercase font-bold text-emerald-700 mb-1">💰 Preț ofertat (CHF)</div>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    value={ofertaPrice}
                    onChange={(e) => setOfertaPrice(e.target.value)}
                    placeholder="ex: 12500"
                    className="h-8 text-sm"
                    data-testid={`oferta-price-${project.id}`}
                  />
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={submitOferta} disabled={busy || (!ofertaFile && (!ofertaPrice || parseFloat(ofertaPrice) <= 0))} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7" data-testid={`pipeline-submit-ofertat-${project.id}`}>
                ✓ Confirmă "Ofertat"
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setOfertaUpload(false); setOfertaFile(null); setOfertaPrice(""); }} className="text-xs h-7">Anulează</Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // 2. CATEGORIA 2: Ofertat — după 7 zile fără răspuns → banner "Întreabă de părere"
  if (cat === "2") {
    const ofertat = project.ofertat_at;
    if (!ofertat) return null;
    const daysSince = daysBetween(today, ofertat);
    const ofertaLink = project.oferta_file_id ? (
      <a
        href={`${api.defaults.baseURL}/uploads/${project.oferta_file_id}`}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="text-[10px] underline font-mono hover:text-purple-900"
        data-testid={`oferta-file-link-${project.id}`}
        title={project.oferta_file_name || "ofertă"}
      >
        📎 {project.oferta_file_name || "ofertă"}
      </a>
    ) : null;
    const priceTag = (!hideChf && project.oferta_price) ? (
      <span className="text-[10px] font-bold bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">💰 {project.oferta_price} CHF</span>
    ) : null;
    if (daysSince < 7) {
      return (
        <div className="mb-3 px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-blue-800 flex items-center gap-2" data-testid={`pipeline-banner-${project.id}`}>
          <span>📤</span>
          <div className="flex-1 text-[11px]">
            <div className="font-bold uppercase tracking-wider">Ofertat acum {daysSince} {daysSince === 1 ? "zi" : "zile"}</div>
            <div className="text-[10px] flex items-center gap-2 flex-wrap">
              Reminder după 7 zile fără răspuns. {ofertaLink} {priceTag}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="mb-3 px-3 py-2 rounded-md bg-purple-50 border border-purple-300 text-purple-800 animate-pulse" data-testid={`pipeline-banner-${project.id}`}>
        <div className="flex items-center gap-2">
          <span>📞</span>
          <div className="flex-1">
            <div className="text-[11px] font-bold uppercase tracking-wider">Întreabă de părerea ofertei</div>
            <div className="text-[10px] flex items-center gap-2 flex-wrap">Au trecut {daysSince} zile fără răspuns. {ofertaLink} {priceTag}</div>
          </div>
        </div>
        {canEdit && !askResp && (
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={() => setAskResp(true)} className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-7" data-testid={`pipeline-ask-${project.id}`}>
              📞 Am întrebat
            </Button>
          </div>
        )}
        {askResp && (
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={() => post(`/projects/${project.id}/oferta-response`, { response: "refused" })} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white text-xs h-7" data-testid={`pipeline-refused-${project.id}`}>
              ✕ Refuzat
            </Button>
            <Button size="sm" onClick={() => post(`/projects/${project.id}/oferta-response`, { response: "accepted" })} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7" data-testid={`pipeline-accepted-${project.id}`}>
              ✓ Acceptat
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAskResp(false)} className="text-xs h-7">Anulează</Button>
          </div>
        )}
      </div>
    );
  }

  // 3. CATEGORIA 3: Acceptat → banner "Anmelduiește SUVA și bagă-l în plan"
  if (cat === "3" && project.oferta_response === "accepted") {
    return (
      <div className="mb-3 rounded-md bg-emerald-50 border border-emerald-300 text-emerald-800" data-testid={`pipeline-banner-${project.id}`}>
        <div className="px-3 py-2 flex items-center gap-2">
          <span className="text-base">🛡️</span>
          <div className="flex-1">
            <div className="text-[11px] font-bold uppercase tracking-wider">Ofertă ACCEPTATĂ ✓</div>
            <div className="text-[10px]">Anmelduiește SUVA, încarcă Meldung-ul și bagă-l în plan.</div>
          </div>
          {canEdit && !suvaUpload && (
            <Button size="sm" onClick={() => setSuvaUpload(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7" data-testid={`pipeline-anmeldui-${project.id}`}>
              🛡️ Anmelduit
            </Button>
          )}
        </div>
        {suvaUpload && (
          <div className="px-3 pb-3 pt-1 border-t border-emerald-300 bg-white/60 space-y-2">
            <div className="text-[11px] font-bold text-emerald-800">📎 Încarcă SUVA Meldung (PDF/imagine)</div>
            {suvaFile ? (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-emerald-700">✓ {suvaFile.original_filename || suvaFile.filename}</span>
                <button type="button" onClick={() => setSuvaFile(null)} className="text-red-600 underline text-[11px]">schimbă</button>
              </div>
            ) : (
              <UploadButton accept="application/pdf,image/*" label="📎 Încarcă SUVA Meldung" testId={`suva-upload-${project.id}`} onUploaded={(f) => setSuvaFile(f)}/>
            )}
            <div className="flex gap-2">
              <Button size="sm" onClick={() => submitFileAction(`/projects/${project.id}/anmeldui-suva`, suvaFile, "SUVA Meldung")} disabled={busy || !suvaFile} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7" data-testid={`pipeline-submit-anmeldui-${project.id}`}>
                ✓ Anmelduit + în plan
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setSuvaUpload(false); setSuvaFile(null); }} className="text-xs h-7">Anulează</Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // 5. CATEGORIA 5: Anmelduit SUVA → countdown spre Sanierungskonzept; ultima zi → "Print Konzept"
  if (cat === "5") {
    const startDate = project.start_date;
    if (!startDate) return null;
    const daysToStart = daysBetween(startDate, today);
    if (daysToStart < 0) return null; // execuția deja începută

    // Ultima zi înainte de execuție
    if (daysToStart <= 1 && !project.konzept_printed_at) {
      return (
        <div className="mb-3 rounded-md bg-red-50 border border-red-400 text-red-800 animate-pulse" data-testid={`pipeline-banner-${project.id}`}>
          <div className="px-3 py-2 flex items-center gap-2">
            <span className="text-base">🖨️</span>
            <div className="flex-1">
              <div className="text-[11px] font-bold uppercase tracking-wider">
                {daysToStart === 0 ? "ASTĂZI execuție — SCOATE LA IMPRIMANTĂ KONZEPTUL!" : "Mâine începe execuția — Print Konzept!"}
              </div>
              <div className="text-[10px]">Sanierungskonzept-ul trebuie printat și încărcat în sistem.</div>
            </div>
            {canEdit && !konzeptUpload && (
              <Button size="sm" onClick={() => setKonzeptUpload(true)} className="bg-red-600 hover:bg-red-700 text-white text-xs h-7" data-testid={`pipeline-konzept-${project.id}`}>
                🖨️ Konzept
              </Button>
            )}
          </div>
          {konzeptUpload && (
            <div className="px-3 pb-3 pt-1 border-t border-red-400 bg-white/60 space-y-2">
              <div className="text-[11px] font-bold text-red-800">📎 Încarcă Sanierungskonzept (PDF/imagine)</div>
              {konzeptFile ? (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-red-700">✓ {konzeptFile.original_filename || konzeptFile.filename}</span>
                  <button type="button" onClick={() => setKonzeptFile(null)} className="text-red-600 underline text-[11px]">schimbă</button>
                </div>
              ) : (
                <UploadButton accept="application/pdf,image/*" label="📎 Încarcă Konzept" testId={`konzept-upload-${project.id}`} onUploaded={(f) => setKonzeptFile(f)}/>
              )}
              <div className="flex gap-2">
                <Button size="sm" onClick={() => submitFileAction(`/projects/${project.id}/konzept-printed`, konzeptFile, "Sanierungskonzept")} disabled={busy || !konzeptFile} className="bg-red-600 hover:bg-red-700 text-white text-xs h-7" data-testid={`pipeline-submit-konzept-${project.id}`}>
                  ✓ Konzept printat
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setKonzeptUpload(false); setKonzeptFile(null); }} className="text-xs h-7">Anulează</Button>
              </div>
            </div>
          )}
        </div>
      );
    }
    // Banner informativ: zile rămase pentru Konzept
    const suvaLink = project.suva_meldung_file_id ? (
      <a href={`${api.defaults.baseURL}/uploads/${project.suva_meldung_file_id}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[10px] underline font-mono">📎 SUVA</a>
    ) : null;
    const konzeptLink = project.konzept_file_id ? (
      <a href={`${api.defaults.baseURL}/uploads/${project.konzept_file_id}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[10px] underline font-mono">📎 Konzept</a>
    ) : null;
    return (
      <div className="mb-3 px-3 py-2 rounded-md bg-sky-50 border border-sky-200 text-sky-800" data-testid={`pipeline-banner-${project.id}`}>
        <div className="flex items-center gap-2">
          <span>📋</span>
          <div className="flex-1 text-[11px]">
            <div className="font-bold uppercase tracking-wider">{daysToStart} {daysToStart === 1 ? "zi rămasă" : "zile rămase"} pentru Sanierungskonzept</div>
            <div className="text-[10px] flex items-center gap-2 flex-wrap">
              Execuție: {startDate}
              {suvaLink}
              {project.konzept_printed_at && <span>· ✓ Konzept printat</span>}
              {konzeptLink}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

function ProjectDetailDialog({ project, onClose, onOpenHours }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/projects/${project.id}/summary`).then((r) => setData(r.data)).catch(() => setData({ status_history: [], bags_log: [] }));
  }, [project.id]);
  const totalBags = (data?.bags_festgebunden || 0) + (data?.bags_schwachgebunden || 0);
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="project-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Folder size={18} className="text-[var(--primary)]" /> {project.name}
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-zinc-500 -mt-1 space-y-0.5">
          {project.adresa && <div>📍 {project.adresa}</div>}
          <div>
            📅 {fmtRoDate(project.start_date) || "—"} → {fmtRoDate(project.end_date) || "—"}
            <span className="ml-2 px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-600 font-semibold">{project.status || "—"}</span>
          </div>
        </div>

        {!data ? (
          <div className="text-sm text-zinc-400 py-6 text-center">Se încarcă…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="project-detail-stats">
              <button type="button" onClick={onOpenHours} className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-left hover:border-[var(--primary)] transition-colors" title="Click pentru raportul detaliat de ore" data-testid="project-detail-hours">
                <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">⏱ Ore lucrate</div>
                <div className="font-display text-xl font-extrabold text-[var(--primary)]">{data.total_hours ?? 0} h</div>
                <div className="text-[10px] text-zinc-400 mt-0.5">detalii →</div>
              </button>
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">📅 Zile lucrate</div>
                <div className="font-display text-xl font-extrabold">{data.days_worked ?? 0}</div>
              </div>
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">👥 Persoane</div>
                <div className="font-display text-xl font-extrabold">{data.persons_count ?? 0}</div>
              </div>
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3" data-testid="project-detail-bags">
                <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">🗑 Saci aruncați</div>
                <div className="font-display text-xl font-extrabold">{totalBags}</div>
                {totalBags > 0 && (
                  <div className="text-[10px] text-zinc-500 mt-0.5">
                    <span className="text-yellow-600 font-semibold">{data.bags_festgebunden} fest</span>
                    {" · "}
                    <span className="text-red-600 font-semibold">{data.bags_schwachgebunden} schwach</span>
                  </div>
                )}
              </div>
            </div>

            {/* 0 ore aici, dar există ore pe șantiere cu nume similar → posibil duplicat */}
            {(data.total_hours ?? 0) === 0 && (data.similar_with_hours || []).length > 0 && (
              <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 text-xs" data-testid="similar-hours-hint">
                <div className="font-bold text-amber-800 mb-1">⚠️ 0 ore aici — dar am găsit ore pontate pe șantiere cu nume similar:</div>
                {(data.similar_with_hours || []).map((s) => (
                  <div key={s.id} className="flex items-center justify-between text-amber-900 font-semibold">
                    <span>{s.kind === "regie" ? "🔁 Regie" : "📁 Proiect"}: {s.name}</span>
                    <span>{s.total_hours} h</span>
                  </div>
                ))}
                <div className="text-amber-700 mt-1">Probabil echipa a pontat acolo (șantier duplicat sau regie cu același nume).</div>
              </div>
            )}

            {/* 🧰 Istoricul seturilor de scule pe acest șantier */}
            <ToolSetsHistorySection project_id={project?.id} title="🧰 Scule folosite pe șantier (istoric)" />

            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-zinc-600 mb-2">📜 Istoric status — cine și când</div>
              {(data.status_history || []).length === 0 ? (
                <div className="text-xs text-zinc-400 border border-dashed border-zinc-200 rounded p-3 text-center">
                  Nicio schimbare de status înregistrată încă. Schimbările viitoare vor apărea aici.
                </div>
              ) : (
                <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1" data-testid="project-status-history">
                  {(data.status_history || []).map((h, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs bg-zinc-50 border border-zinc-100 rounded px-2.5 py-1.5">
                      <span className={`px-2 py-0.5 rounded-full font-bold text-[10px] whitespace-nowrap ${
                        /demontat/i.test(h.status || "") && !/de demontat/i.test(h.status || "") ? "bg-zinc-200 text-zinc-700"
                        : /de demontat|finalizat/i.test(h.status || "") ? "bg-orange-100 text-orange-700"
                        : "bg-emerald-100 text-emerald-700"
                      }`}>{h.status || "—"}</span>
                      <span className="flex-1 min-w-0 truncate text-zinc-700">
                        👤 <strong>{h.changed_by || "—"}</strong>
                      </span>
                      <span className="text-zinc-500 whitespace-nowrap">{h.changed_at ? fmtRoDateTime(h.changed_at) : "—"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {(data.bags_log || []).length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-zinc-600 hover:text-zinc-900 font-semibold">
                  🗑 Jurnal saci ({data.bags_log.length} {data.bags_log.length === 1 ? "înregistrare" : "înregistrări"})
                </summary>
                <div className="mt-1.5 space-y-1 max-h-40 overflow-y-auto pr-1" data-testid="project-bags-log">
                  {data.bags_log.map((b) => (
                    <div key={b.id} className="flex items-center gap-2 bg-zinc-50 border border-zinc-100 rounded px-2.5 py-1.5">
                      <span className="font-semibold">
                        <span className="text-yellow-600">{b.festgebunden || 0}f</span>
                        <span className="text-zinc-300"> / </span>
                        <span className="text-red-600">{b.schwachgebunden || 0}s</span>
                      </span>
                      <span className="flex-1 min-w-0 truncate text-zinc-600">👤 {b.user_name || "—"}{b.note ? ` · ${b.note}` : ""}</span>
                      <span className="text-zinc-400 whitespace-nowrap">{b.created_at ? fmtRoDateTime(b.created_at) : ""}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="project-detail-close">Închide</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RegieDetailDialog({ regie, onClose }) {
  const [data, setData] = useState(null);
  const [uploadingWeek, setUploadingWeek] = useState(null);
  const [targetWeek, setTargetWeek] = useState(null);
  const [expandedWeek, setExpandedWeek] = useState(null); // week_start → detaliere zilnică
  const [rotations, setRotations] = useState({}); // rep.id → grade rotire
  const rotate = (id, delta) => setRotations((r) => ({ ...r, [id]: ((r[id] || 0) + delta + 360) % 360 }));
  const isPdfRep = (rep) => /\.pdf$/i.test(rep.original_filename || "");
  const fileRef = useRef(null);
  const load = () => api.get(`/regii/${regie.id}/weekly-report`).then((r) => setData(r.data)).catch(() => setData({ weeks: [] }));
  useEffect(() => { load(); }, [regie.id]);

  const pickFiles = (w) => { setTargetWeek(w); fileRef.current?.click(); };
  const uploadFilesForWeek = async (w, files) => {
    if (!files.length || !w) return;
    setUploadingWeek(w.week_start);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("report_type", "wochen");
        fd.append("start_date", w.week_start);
        fd.append("end_date", w.week_end || w.week_start);
        fd.append("regie_id", regie.id);
        const pid = w.persons?.[0]?.id;
        if (pid) fd.append("person_id", pid);
        await api.post("/signed-reports", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(files.length === 1 ? "Raport încărcat" : `${files.length} rapoarte încărcate`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la încărcarea raportului");
    } finally {
      setUploadingWeek(null);
      setTargetWeek(null);
    }
  };
  const onFilesChosen = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!targetWeek) return;
    await uploadFilesForWeek(targetWeek, files);
  };
  const repUrl = (rid) => {
    const token = tokenStore.get() || "";
    return `${API}/signed-reports/${rid}/file?token=${encodeURIComponent(token)}`;
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="regie-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ background: regie.color || "#F59E0B" }} />
            {regie.name} — ore pe săptămâni
          </DialogTitle>
        </DialogHeader>
        {data && (data.weeks || []).length > 0 && (
          <div className="flex items-center gap-3 text-sm bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2" data-testid="regie-totals">
            <span>⏱ Total: <strong className="text-[var(--primary)]">{data.total_hours} h</strong></span>
            <span className="text-zinc-300">|</span>
            <span>💶 Total spese: <strong className="text-emerald-700">{data.total_spese_days}</strong> <span className="text-xs text-zinc-500">(1 om × 1 zi = 1 spesă)</span></span>
          </div>
        )}
        {!data ? (
          <div className="text-sm text-zinc-400 py-6 text-center">Se încarcă…</div>
        ) : (data.weeks || []).length === 0 ? (
          <div className="text-sm text-zinc-400 border border-dashed border-zinc-200 rounded p-6 text-center">
            Nicio oră înregistrată pe această regie încă.
          </div>
        ) : (
          <div className="space-y-3" data-testid="regie-weeks-list">
            {data.weeks.map((w) => (
              <DropZone
                key={w.week_start}
                onFiles={(files) => uploadFilesForWeek(w, files)}
                disabled={uploadingWeek === w.week_start}
                className={`card-flat p-3 border-l-4 ${w.invoiced ? "border-l-emerald-500 bg-emerald-50/40" : w.billable ? "border-l-red-500 bg-red-50/40" : "border-l-[var(--orange-bila,#F59E0B)]"}`}
                testId={`regie-week-${w.week_start}`}
              >
                <div
                  className="flex items-center gap-2 flex-wrap cursor-pointer select-none"
                  onClick={() => setExpandedWeek((cur) => (cur === w.week_start ? null : w.week_start))}
                  title="Click pentru detalierea pe zile"
                  data-testid={`regie-week-toggle-${w.week_start}`}
                >
                  <span className="font-display font-bold text-sm">📅 {fmtRoDate(w.week_start)} – {fmtRoDate(w.week_end)}</span>
                  <span className="text-[10px] text-zinc-400 font-semibold">{expandedWeek === w.week_start ? "▲ ascunde zilele" : "▼ vezi zilele"}</span>
                  {w.invoiced ? (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500 text-white" data-testid={`regie-invoiced-${w.week_start}`}>✓ FACTURATĂ</span>
                  ) : w.billable ? (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500 text-white" data-testid={`regie-uninvoiced-${w.week_start}`}>✗ NEFACTURATĂ</span>
                  ) : (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-600" title="Săptămâna nu s-a încheiat încă">în curs</span>
                  )}
                  <span className="ml-auto flex items-center gap-2">
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800" data-testid={`regie-spese-${w.week_start}`} title="1 om × 1 zi lucrată = 1 spesă">💶 {w.spese_days} spese</span>
                    <span className="font-display text-lg font-extrabold text-[var(--primary)]">{w.total_hours} h</span>
                  </span>
                </div>
                {expandedWeek === w.week_start && (w.days || []).length > 0 && (
                  <div className="mt-2 overflow-x-auto" data-testid={`regie-days-${w.week_start}`}>
                    <table className="text-xs border-collapse w-full">
                      <thead>
                        <tr>
                          <th className="text-left px-2 py-1.5 border border-zinc-200 bg-zinc-100 font-bold">👷 Nume</th>
                          {w.days.map((d) => (
                            <th key={d.date} className="px-2 py-1.5 border border-zinc-200 bg-zinc-100 font-bold whitespace-nowrap text-center">{fmtRoDate(d.date)}</th>
                          ))}
                          <th className="px-2 py-1.5 border border-zinc-200 bg-zinc-100 font-bold text-center">Σ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {w.persons.map((p) => (
                          <tr key={p.id}>
                            <td className="px-2 py-1 border border-zinc-200 font-semibold whitespace-nowrap">{p.name}</td>
                            {w.days.map((d) => {
                              const f = (d.persons || []).find((x) => x.id === p.id);
                              return (
                                <td key={d.date} className={`px-2 py-1 border border-zinc-200 text-center ${f ? "font-bold text-[var(--primary)]" : "text-zinc-300"}`}>
                                  {f ? `${f.hours}h` : "–"}
                                </td>
                              );
                            })}
                            <td className="px-2 py-1 border border-zinc-200 text-center font-extrabold">{p.hours}h</td>
                          </tr>
                        ))}
                        <tr>
                          <td className="px-2 py-1 border border-zinc-200 bg-zinc-50 font-bold">Total zi</td>
                          {w.days.map((d) => (
                            <td key={d.date} className="px-2 py-1 border border-zinc-200 bg-zinc-50 text-center font-extrabold">{d.total_hours}h</td>
                          ))}
                          <td className="px-2 py-1 border border-zinc-200 bg-zinc-50 text-center font-black text-[var(--primary)]">{w.total_hours}h</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
                {expandedWeek === w.week_start && (w.days || []).length === 0 && (
                  <div className="mt-2 text-[11px] text-zinc-400 italic">Nicio oră detaliată pe zile în această săptămână.</div>
                )}
                {w.persons.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap mt-2 text-xs">
                    {w.persons.map((p) => (
                      <span key={p.id} className="px-1.5 py-0.5 rounded bg-zinc-50 border border-zinc-200">
                        <strong>{p.name}</strong> · {p.hours}h · <span className="text-emerald-700">{p.days} {p.days === 1 ? "zi" : "zile"}</span>
                      </span>
                    ))}
                  </div>
                )}
                {(w.cars || []).length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap mt-2 text-xs" data-testid={`regie-cars-${w.week_start}`}>
                    {w.cars.map((c) => (
                      <span key={c} className="px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-900">🚗 {c}</span>
                    ))}
                  </div>
                )}
                {(w.reports || []).length > 0 && (
                  <div className="mt-2 space-y-1">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">📎 Rapoarte încărcate ({w.reports.length})</div>
                    {w.reports.map((rep) => (
                      <a
                        key={rep.id}
                        href={repUrl(rep.id)}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-100 rounded px-2 py-1 hover:border-emerald-400 transition-colors"
                        data-testid={`regie-report-${rep.id}`}
                      >
                        <span className="text-emerald-700 font-semibold">{rep.report_type === "wochen" ? "Raport ore săptămânal" : "Raport ore zilnic"}</span>
                        <span className="flex-1 min-w-0 truncate text-zinc-600">👤 {rep.person_name || rep.uploaded_by_name || "—"} · {rep.original_filename}</span>
                        <span className="text-zinc-400 whitespace-nowrap">{fmtRoDate(rep.start_date)}{rep.end_date && rep.end_date !== rep.start_date ? ` – ${fmtRoDate(rep.end_date)}` : ""}</span>
                      </a>
                    ))}
                  </div>
                )}
                {expandedWeek === w.week_start && (w.reports || []).length > 0 && (
                  <div className="mt-2 space-y-2" data-testid={`report-previews-${w.week_start}`}>
                    {w.reports.map((rep) => (
                      <div key={`prev-${rep.id}`} className="border border-zinc-200 rounded-md p-2 bg-zinc-50" data-testid={`report-preview-${rep.id}`}>
                        <div className="flex items-center gap-2 mb-1.5 text-xs flex-wrap">
                          <span className="font-semibold truncate">📄 {rep.original_filename}</span>
                          <span className="text-zinc-400">· {rep.person_name || rep.uploaded_by_name || "—"}</span>
                          {!isPdfRep(rep) && (
                            <span className="ml-auto flex gap-1">
                              <button type="button" onClick={() => rotate(rep.id, -90)} className="px-2 py-0.5 border border-zinc-300 rounded bg-white hover:bg-zinc-100 font-bold" title="Rotește la stânga" data-testid={`rotate-left-${rep.id}`}>↺</button>
                              <button type="button" onClick={() => rotate(rep.id, 90)} className="px-2 py-0.5 border border-zinc-300 rounded bg-white hover:bg-zinc-100 font-bold" title="Rotește la dreapta" data-testid={`rotate-right-${rep.id}`}>↻</button>
                            </span>
                          )}
                        </div>
                        {isPdfRep(rep) ? (
                          <iframe title={rep.id} src={repUrl(rep.id)} className="w-full h-96 border border-zinc-200 rounded bg-white" />
                        ) : (
                          <div className="flex justify-center items-center overflow-auto bg-white border border-zinc-200 rounded" style={{ minHeight: "200px" }}>
                            <img
                              src={repUrl(rep.id)}
                              alt={rep.original_filename}
                              style={{ transform: `rotate(${rotations[rep.id] || 0}deg)`, maxHeight: "420px", maxWidth: "100%", transition: "transform 0.25s ease" }}
                              data-testid={`report-img-${rep.id}`}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {(w.reports || []).length === 0 && w.total_hours > 0 && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] text-amber-600">⚠ Niciun raport încărcat pentru această săptămână</span>
                    <button
                      onClick={() => pickFiles(w)}
                      disabled={uploadingWeek === w.week_start}
                      className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                      data-testid={`regie-upload-report-${w.week_start}`}
                    >
                      <Upload size={11} /> {uploadingWeek === w.week_start ? "Se încarcă..." : "Încarcă raport"}
                    </button>
                  </div>
                )}
                {(w.reports || []).length > 0 && (
                  <button
                    onClick={() => pickFiles(w)}
                    disabled={uploadingWeek === w.week_start}
                    className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border border-zinc-300 text-zinc-600 hover:border-zinc-500 hover:text-zinc-900 transition-colors disabled:opacity-50"
                    data-testid={`regie-upload-more-${w.week_start}`}
                  >
                    <Upload size={11} /> {uploadingWeek === w.week_start ? "Se încarcă..." : "+ Adaugă alt raport"}
                  </button>
                )}
              </DropZone>
            ))}
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="hidden"
          onChange={onFilesChosen}
          data-testid="regie-report-file-input"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="regie-detail-close">Închide</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { ProjectListRow, HoursReportDialog, ProjectDocsDialog, PipelineBanner, ProjectDetailDialog, RegieDetailDialog };
